/**
 * Route Prefetcher Utility
 * Handles prefetching of route resources on hover
 */

interface PrefetchOptions {
  delay?: number; // Delay in milliseconds before prefetching
  priority?: 'high' | 'low' | 'auto';
}

interface RouteModule {
  default: React.ComponentType<any>;
}

class RoutePrefetcher {
  private prefetchedRoutes: Set<string> = new Set();
  private prefetchTimers: Map<string, NodeJS.Timeout> = new Map();
  private routeModules: Map<string, () => Promise<RouteModule>> = new Map();

  /**
   * Register route modules for prefetching
   */
  registerRoutes(routes: Record<string, () => Promise<RouteModule>>): void {
    Object.entries(routes).forEach(([path, loader]) => {
      this.routeModules.set(path, loader);
    });
  }

  /**
   * Prefetch a route's resources
   */
  async prefetch(path: string, options: PrefetchOptions = {}): Promise<void> {
    const { delay = 200, priority = 'low' } = options;

    // Avoid duplicate prefetches
    if (this.prefetchedRoutes.has(path)) {
      return;
    }

    // Clear any existing timer for this path
    const existingTimer = this.prefetchTimers.get(path);
    if (existingTimer) {
      clearTimeout(existingTimer);
    }

    // Set up delayed prefetch
    const timer = setTimeout(async () => {
      try {
        // Get the route module loader
        const loader = this.routeModules.get(path);
        
        if (loader) {
          // Prefetch the module
          await loader();
          this.prefetchedRoutes.add(path);
        } else {
          // If no specific loader, try to prefetch via link element
          this.prefetchViaLink(path, priority);
        }
      } catch (error) {
        console.error(`Failed to prefetch route ${path}:`, error);
      } finally {
        this.prefetchTimers.delete(path);
      }
    }, delay);

    this.prefetchTimers.set(path, timer);
  }

  /**
   * Prefetch via link element (for static resources)
   */
  private prefetchViaLink(path: string, priority: 'high' | 'low' | 'auto'): void {
    const link = document.createElement('link');
    link.rel = 'prefetch';
    link.href = path;
    link.as = 'document';
    
    if (priority !== 'auto') {
      link.setAttribute('fetchpriority', priority);
    }

    document.head.appendChild(link);
    this.prefetchedRoutes.add(path);
  }

  /**
   * Cancel a pending prefetch
   */
  cancelPrefetch(path: string): void {
    const timer = this.prefetchTimers.get(path);
    if (timer) {
      clearTimeout(timer);
      this.prefetchTimers.delete(path);
    }
  }

  /**
   * Check if a route has been prefetched
   */
  isPrefetched(path: string): boolean {
    return this.prefetchedRoutes.has(path);
  }

  /**
   * Prefetch most visited pages
   */
  async prefetchMostVisited(paths: string[]): Promise<void> {
    // Prefetch in sequence with a small delay between each
    for (const path of paths) {
      if (!this.prefetchedRoutes.has(path)) {
        await this.prefetch(path, { delay: 0, priority: 'low' });
        // Small delay to avoid overwhelming the network
        await new Promise(resolve => setTimeout(resolve, 100));
      }
    }
  }

  /**
   * Clear all prefetched routes tracking
   */
  clear(): void {
    // Clear all pending timers
    this.prefetchTimers.forEach(timer => clearTimeout(timer));
    this.prefetchTimers.clear();
    this.prefetchedRoutes.clear();
  }
}

// Export singleton instance
export const routePrefetcher = new RoutePrefetcher();

// Export helper functions
export const prefetchRoute = (path: string, options?: PrefetchOptions) => {
  routePrefetcher.prefetch(path, options);
};

export const cancelPrefetch = (path: string) => {
  routePrefetcher.cancelPrefetch(path);
};

export const isPrefetched = (path: string) => {
  routePrefetcher.isPrefetched(path);
};

export const prefetchMostVisited = (paths: string[]) => {
  routePrefetcher.prefetchMostVisited(paths);
};

// Most visited pages configuration
export const MOST_VISITED_PAGES = [
  '/breeders',
  '/breeds',
  '/ai-expert',
  '/dashboard',
  '/website-builder',
];
